// ============================================================
// HistorialReprogramacionesDrawer.tsx
// Bottom sheet (mismo patrón que el resto de dialogs) que muestra
// el historial completo de reprogramaciones de una cita.
// ============================================================

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ArrowRight, User, Briefcase, ChevronLeft, X } from 'lucide-react';
import type { ReprogramacionHistorial } from '@/components/types/typeCalendar';

interface HistorialReprogramacionesDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  historial: ReprogramacionHistorial[];
  nombreServicio?: string | null;
}

const formatFecha = (fecha: string) =>
  new Date(`${fecha}T00:00:00`).toLocaleDateString('es-ES', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  });

const formatFechaCreacion = (fechaISO: string) =>
  new Date(fechaISO).toLocaleDateString('es-ES', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

const formatHora = (hora: string) => (hora ? hora.slice(0, 5) : '');

export default function HistorialReprogramacionesDrawer({
  open,
  onOpenChange,
  historial,
  nombreServicio,
}: HistorialReprogramacionesDrawerProps) {
  const total = historial.length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="
          z-[60] p-0 border border-gray-200 dark:border-white/10 bg-white dark:bg-[#1c1c1e]
          [&>button:last-child]:hidden
          flex flex-col
          !bottom-0 !left-0 !top-auto !translate-x-0 !translate-y-0
          w-full max-w-none max-h-[85vh] rounded-t-2xl rounded-b-none
          data-[state=open]:slide-in-from-bottom data-[state=closed]:slide-out-to-bottom
          data-[state=open]:duration-300 data-[state=closed]:duration-200
          sm:!bottom-auto sm:!left-1/2 sm:!top-1/2 sm:!-translate-x-1/2 sm:!-translate-y-1/2
          sm:w-[95%] sm:max-w-md sm:mx-auto sm:h-auto sm:max-h-[85vh] sm:rounded-xl sm:shadow-xl
        "
      >
        <DialogHeader className="px-5 pt-4 pb-3 border-b border-gray-100 dark:border-white/10 relative shrink-0">
          <button
            onClick={() => onOpenChange(false)}
            className="absolute left-4 top-3.5 w-8 h-8 flex items-center justify-center rounded-full text-gray-500 hover:text-gray-800 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
            aria-label="Volver"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>

          <DialogTitle className="text-sm font-semibold text-[#103254] dark:text-blue-300 text-center">
            Historial de cambios
          </DialogTitle>
          {nombreServicio && (
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 text-center">
              {nombreServicio} · {total} {total === 1 ? 'cambio' : 'cambios'}
            </p>
          )}

          <button
            onClick={() => onOpenChange(false)}
            className="absolute right-4 top-3.5 w-8 h-8 flex items-center justify-center rounded-full text-gray-500 hover:text-gray-800 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
            aria-label="Cerrar"
          >
            <X className="w-4 h-4" />
          </button>
        </DialogHeader>

        <div className="overflow-y-auto px-4 py-4 space-y-3 flex-1">
          {historial.map((mov, idx) => {
            const esCliente = mov.reprogramado_por === 'cliente';
            const esMasReciente = idx === 0;

            return (
              <div
                key={`${mov.fecha_creacion}-${idx}`}
                className={`rounded-xl border p-3.5 transition-colors ${
                  esMasReciente
                    ? 'border-[#103254]/20 bg-[#103254]/[0.03] dark:border-blue-800/40 dark:bg-blue-900/10'
                    : 'border-gray-100 dark:border-white/10'
                }`}
              >
                <div className="flex items-center justify-between mb-2.5">
                  <span
                    className={`inline-flex items-center gap-1.5 text-[11px] font-semibold px-2 py-1 rounded-full border ${
                      esCliente
                        ? 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/20 dark:text-blue-400 dark:border-blue-800'
                        : 'bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-900/20 dark:text-purple-400 dark:border-purple-800'
                    }`}
                  >
                    {esCliente ? (
                      <User className="w-3 h-3" />
                    ) : (
                      <Briefcase className="w-3 h-3" />
                    )}
                    {esCliente ? 'Cliente' : 'Trabajador'}
                  </span>
                  <span className="text-[11px] text-gray-400 dark:text-gray-500">
                    {formatFechaCreacion(mov.fecha_creacion)}
                  </span>
                </div>

                <div className="flex items-center gap-2 text-sm">
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-0.5">
                      Antes
                    </p>
                    <p className="font-medium text-gray-500 dark:text-gray-400 line-through truncate">
                      {formatFecha(mov.fecha_anterior)} ·{' '}
                      {formatHora(mov.hora_anterior)}
                    </p>
                  </div>
                  <ArrowRight className="w-3.5 h-3.5 text-gray-300 dark:text-gray-600 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-0.5">
                      Después
                    </p>
                    <p className="font-semibold text-gray-800 dark:text-gray-100 truncate">
                      {formatFecha(mov.fecha_nueva)} ·{' '}
                      {formatHora(mov.hora_nueva)}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}